import { useCallback, useEffect, useReducer, useState } from 'react';
import { emotionOf, initialEmotion, nextChangeAt, reduceEmotion, type Emotion, type EmotionInput } from './emotion';

/**
 * The emotion reducer (Task 13) on a clock: a hold that ends or sleepy that begins re-renders without any input.
 * While the panel is open, a pointer press or a key anywhere is activity (D91).
 */
export function useEmotion({ open, now = Date.now }: { open: boolean; now?: () => number }):
  { emotion: Emotion; feel: (input: EmotionInput) => void } {
  const [state, setState] = useState(() => initialEmotion(now()));
  const [, tick] = useReducer((n: number) => n + 1, 0);
  const feel = useCallback((input: EmotionInput) => setState((s) => reduceEmotion(s, input, now())), [now]);
  const at = now();
  const shown = emotionOf(state, at);
  // Sleepy counts only while the panel is open (D91); closed, Hana rests at idle on the button.
  const emotion: Emotion = !open && shown === 'sleepy' ? 'idle' : shown;
  const next = nextChangeAt(state, at);
  useEffect(() => {
    if (next === null) return undefined;
    const id = window.setTimeout(tick, Math.max(0, next - now()) + 1);
    return () => window.clearTimeout(id);
  }, [next, now]);
  useEffect(() => {
    if (!open) return undefined;
    const wake = () => feel({ kind: 'interaction' });
    document.addEventListener('pointerdown', wake);
    document.addEventListener('keydown', wake);
    return () => {
      document.removeEventListener('pointerdown', wake);
      document.removeEventListener('keydown', wake);
    };
  }, [open, feel]);
  return { emotion, feel };
}
